import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import Slice9Panel from "../../../../../lib/html5-software-library/src/ranj/components/scale9/Slice9Panel";
import TextUtil from "../../../../../lib/html5-software-library/src/ranj/components/text/TextUtil";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import Anchor from "../../../../../lib/html5-software-library/src/ranj/layout/Anchor";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import SoundType from "../../../core/sound/util/SoundType";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import TextTypeWriterAnimation from "../../../core/ui/animation/typewriters/TextTypeWriterAnimation";
import DebugModel from "../../debug/model/DebugModel";
import DisplayObjectUtil from "../../util/DisplayObjectUtil";
import MovieClipUtil from "../../util/MovieClipUtil";

export default class CharacterSpeechMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {OverlayFactory} overlayFactory 
     * @param {AssetLibrary} assetLibrary
     * @param {RevisableLanguagePack} languagePack
     * @param {SoundManager} soundManager
     */
    constructor(target, dispatcher, layoutManager, overlayFactory, assetLibrary, languagePack, soundManager) {
        super(target, dispatcher, layoutManager, overlayFactory);

        this._assetLibrary = assetLibrary;
        this._languagePack = languagePack;
        this._soundManager = soundManager;

        this._characterName = "";
        this._offsetY = 0;
        /** @type {TrackerFeedback[]} */
        this._trackerFeedbacks = [];
    }

    initialize() {
        super.initialize();

        this._target.aiBotResponse.responseText.initialY = this._target.aiBotResponse.responseText.y;
        this._target.aiBotResponse.responseText.initialHeight = this._target.aiBotResponse.responseText.getBounds().height;

        this._actionSlice9Panel = new Slice9Panel(this._target.actionPanel, MovieClipUtil.getFirstBitmapChild(this._target.actionPanel), {
            mode: Slice9Panel.VERTICAL,
            pixelPerfect: true,
            overlap: false,
        });
        this._dialogueSlice9Panel = new Slice9Panel(this._target.conversationPanel, MovieClipUtil.getFirstBitmapChild(this._target.conversationPanel), {
            mode: Slice9Panel.VERTICAL,
            pixelPerfect: true,
            overlap: false,
        });
        this._trackerSlice9Panel = new Slice9Panel(this._target.aiBotResponse, MovieClipUtil.getFirstBitmapChild(this._target.aiBotResponse), {
            mode: Slice9Panel.VERTICAL,
            pixelPerfect: true,
            overlap: false,
        });

        this._target.initialY = this._target.y;
        this._target.conversationPanel.initialY = this._target.conversationPanel.y;
        this._target.aiBotResponse.initialY = this._target.aiBotResponse.y;
        this._target.actionPanel.initialY = this._target.actionPanel.y;

        this._target.profilePicture.charImageContainer.removeAllChildren();
        this._target.conversationPanel.responseText.text = "";
        this._target.profilePicture.nameTextField.text = "";
        this._target.aiBotResponse.visible = false;
    }

    destroy() {
        super.destroy();
    }

    async showPanels(delayInMs = 0) {
        const duration = DebugModel.fasterAnimations ? 150 : 450;
        await createjs.Tween.get(this._target.actionPanel)
            .wait(delayInMs)
            .to({
                alpha: 1,
            }, duration, createjs.Ease.sineInOut)
            .toPromise();

        const targetText = this._target.conversationPanel.responseText.text;
        this._target.conversationPanel.responseText.text = "";

        await createjs.Tween.get(this._target.conversationPanel)
            .wait(delayInMs)
            .to({
                alpha: 1,
            }, duration, createjs.Ease.sineInOut)
            .toPromise();

        await this._typeWriteText(this._target.conversationPanel.responseText, targetText);
    }

    async _typeWriteText(textField, targetText = "") {
        const typewriteSoundId = this._soundManager.playSound(SoundType.TYPEWRITER_GENERIC, null, null, 0, 0, -1);
        await new Promise(resolve => {
            textField.text = "";

            const timePerCharacter = DebugModel.fasterAnimations ? 0.0001 : 0.015;
            const duration = targetText.length * timePerCharacter;
            TextTypeWriterAnimation.animate(textField, targetText, duration, 0, resolve);
        });
        this._soundManager.stopSound(typewriteSoundId, 50);
    }

    async hidePanels(delayInMs = 0) {
        const duration = DebugModel.fasterAnimations ? 150 : 450;
        createjs.Tween.get(this._target.conversationPanel)
            .wait(delayInMs)
            .to({
                alpha: 0,
            }, duration, createjs.Ease.sineOut);

        await createjs.Tween.get(this._target.actionPanel)
            .wait(delayInMs)
            .to({
                alpha: 0,
            }, duration, createjs.Ease.sineOut)
            .toPromise();
    }

    async hideProfilePicture() {
        const duration = DebugModel.fasterAnimations ? 150 : 450;
        await createjs.Tween.get(this._target.profilePicture)
            .to({
                scale: .5,
                alpha: 0,
            }, duration, createjs.Ease.sineIn)
            .toPromise();
    }

    async showProfilePicture(delay = 0, punch = false) {
        if (punch) {
            this._target.profilePicture.scale = .5;
        }

        const duration = DebugModel.fasterAnimations ? 150 : 450;
        await createjs.Tween.get(this._target.profilePicture)
            .wait(delay)
            .to({
                scale: 1,
                alpha: 1,
            }, duration, createjs.Ease.backOut)
            .toPromise();
    }

    async startLoadingAnimation() {
        this._loadingTween = createjs.Tween.get(this._target.profilePicture, { loop: -1, bounce: true })
            .to({
                scale: 0.9,
            }, 1000, createjs.Ease.sineInOut);
    }

    async stopLoadingAnimation() {
        if (this._loadingTween) {
            this._loadingTween.paused = true;
        }

        await createjs.Tween.get(this._target.profilePicture)
            .to({
                scale: 1,
            }, 500, createjs.Ease.sineInOut)
            .toPromise();
    }

    async hideAll() {
        createjs.Tween.get(this._target.profilePicture)
            .to({
                alpha: 0,
            }, 150, createjs.Ease.sineIn)
            .toPromise();
        createjs.Tween.get(this._target.actionPanel)
            .to({
                alpha: 0,
            }, 150, createjs.Ease.sineIn)
            .toPromise();
        createjs.Tween.get(this._target.aiBotResponse)
            .to({
                alpha: 0,
            }, 150, createjs.Ease.sineIn)
            .toPromise();
        await createjs.Tween.get(this._target.conversationPanel)
            .to({
                alpha: 0,
            }, 150, createjs.Ease.sineIn)
            .toPromise();
    }

    hideAllInstant() {
        this._target.conversationPanel.alpha = 0;
        this._target.actionPanel.alpha = 0;
        this._target.profilePicture.alpha = 0;
    }

    setAction(value) {
        if (!value) {
            this._target.actionPanel.visible = false;
            return;
        }
        this._target.actionPanel.visible = true;

        this._target.actionPanel.responseText.text = value?.trim();
        this._actionSlice9Panel.update();

        // Anchors slice 9 panel to the bottom.
        this._target.actionPanel.y = this._target.actionPanel.initialY - this._actionSlice9Panel.getDeltaHeight();
    }

    setSpeech(value) {
        if (!value) {
            this._target.conversationPanel.visible = false;
            return;
        }

        this._target.conversationPanel.responseText.text = value?.trim();
        this._dialogueSlice9Panel.update();

        // Slice 9 panels are anchored to the top by default.
    }

    setName(name) {
        this._characterName = name;
        this._target.profilePicture.nameTextField.text = name?.trim();
    }

    setImage(imageId) {
        this._target.profilePicture.charImageContainer.removeAllChildren();

        // Get asset, convert to bitmap, set anchor to center, add as child
        const image = this._assetLibrary.getAsset(imageId);

        if (!image) {
            console.warn(`Image is does not exist. You tried: ${imageId}`);
            return;
        }

        const bitmap = new createjs.Bitmap(image);
        DisplayObjectUtil.anchorDisplayObject(bitmap, Anchor.C);
        this._target.profilePicture.charImageContainer.addChild(bitmap);
    }

    setOffsetPosY(offsetY) {
        this._offsetY = offsetY;
        this._target.y = this._target.initialY + this._offsetY;
    }

    addCharacterTrackerFeedback(trackerId, oldValue, newValue) {
        if (oldValue === newValue) {
            return;
        }

        this._trackerFeedbacks.push(new TrackerFeedback(trackerId, oldValue, newValue));
    }

    setCharacterTrackerFeedback(trackerId, oldValue, newValue) {
        // NOTE: Previously we were showing all tracker updates one by one, but that was hard to follow for players.
        // So we opted for showing one generic message.
        trackerId = "generic";

        const increased = newValue - oldValue > 0;
        const lpTextModifier = increased ? "improved" : "decreased";
        const trackerText = this._languagePack.getText(`trackers.${trackerId}.${lpTextModifier}`);
        this._target.aiBotResponse.responseText.text = trackerText.replace(/{{characterName}}/g, this._characterName);

        TextUtil.centerTextVertically(this._target.aiBotResponse.responseText, this._target.aiBotResponse.responseText.initialY, this._target.aiBotResponse.responseText.initialHeight);

        const frameName = increased ? `${trackerId}Up` : `${trackerId}Down`;
        this._target.aiBotResponse.pijlenUpDown.gotoAndStop(frameName);
    }

    clearCharacterTrackerFeedbacks() {
        this._trackerFeedbacks = [];
    }

    async showCharacterTrackerFeedbacks() {
        if (this._trackerFeedbacks.length === 0) {
            return Promise.resolve();
        }

        this._target.aiBotResponse.alpha = 0;
        this._target.aiBotResponse.visible = true;
        this._target.aiBotResponse.y = this._dialogueSlice9Panel.getDeltaHeight() + this._target.aiBotResponse.initialY;

        // NOTE: Previously we were showing all tracker updates one by one, but that was hard to follow for players.
        // So we opted for showing one generic message.
        // However, I kept the old system (list on updates) available.
        const trackerFeedback = this._trackerFeedbacks[0];
        this.setCharacterTrackerFeedback(trackerFeedback.trackerId, trackerFeedback.oldValue, trackerFeedback.newValue);

        // Play sound.
        const increased = trackerFeedback.newValue > trackerFeedback.oldValue;
        this._soundManager.playSound(increased ? SoundType.TRACKER_INCREASE : SoundType.TRACKER_DECREASE);

        const duration = DebugModel.fasterAnimations ? 150 : 450;
        await createjs.Tween.get(this._target.aiBotResponse)
            .to({
                alpha: 1,
            }, duration, createjs.Ease.sineOut)
            .toPromise();

        this._target.aiBotResponse.pijlenUpDown.pijlAni.play();
    }

    async hideCharacterTrackerFeedback() {
        if (this._target.aiBotResponse.alpha <= .01) {
            this._target.aiBotResponse.alpha = 0;
            return Promise.resolve();
        }

        const duration = DebugModel.fasterAnimations ? 150 : 450;
        await createjs.Tween.get(this._target.aiBotResponse)
            .to({
                alpha: 0,
            }, duration, createjs.Ease.sineIn)
            .toPromise();
    }
}

class TrackerFeedback {
    constructor(trackerId, oldValue, newValue) {
        this.trackerId = trackerId;
        this.oldValue = oldValue;
        this.newValue = newValue;
    }
}